import { FC, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { useChartsContext } from "contexts/ChartContext";
import { firstSwatchedSegmentEnd } from "entities/charts/eva/translators/mpcAxisLegend";
import { updateChartByPath } from "store/features";
import { useAxisGridlineStore } from "stores/axisGridline/useAxisGridlineStore";
import { useAxisNameStore } from "stores/axisName/useAxisNameStore";
import { useIsDashboardScreenshotMode } from "stores/dashboardScreenshot/useIsDashboardScreenshotMode";
import styled from "styled-components/macro";

import { useChartEntities } from "hooks/charts";

import { AxisProductSegment } from "models/chart";

import {
  MPC_X_AXIS_TITLE_KEYS,
  MPC_Y_AXIS_TITLE_KEYS
} from "components/screenshot/constants";
import { useScreenshotContext, useScreenshotDispatch } from "components/screenshot/hooks";
import { getEffectiveAxisNameId } from "components/screenshot/utils";

import { Portal } from "../../ui";
import { ChartLegend } from "../legend";
import { dismissAllChartToasts } from "./ChartToast";

type ScreenshotT = {
  containerId: string;
};

const Screenshot: FC<ScreenshotT> = ({ containerId }) => {
  const dispatch = useDispatch();

  const screenshotDispatch = useScreenshotDispatch();
  const { id } = useChartsContext();
  const {
    bounds,
    screenshot,
    chartLegend,
    request,
    apiResponse,
    options,
    multiProduct,
    dataRangeSelect
  } = useChartEntities(id);
  const dataRange = dataRangeSelect?.properties?.range;

  const effectiveAxisNameId = getEffectiveAxisNameId(
    id,
    multiProduct?.active,
    request?.chartSetting?.product
  );

  const { settings, widget } = useScreenshotContext();

  const loadFromStorage = useAxisGridlineStore((s) => s.loadFromStorage);
  const resetToNull = useAxisGridlineStore((s) => s.resetToNull);

  const loadAxisNamesFromStorage = useAxisNameStore((s) => s.loadFromStorage);
  const axisNameResetToNull = useAxisNameStore((s) => s.resetToNull);
  const axisNameResetKeys = useAxisNameStore((s) => s.resetKeys);

  // Load persisted gridline and axis name state once on mount so both stores
  // are ready before any screenshot panel opens for this chart.
  useEffect(() => {
    loadFromStorage(id);
  }, [id, loadFromStorage]);

  useEffect(() => {
    loadAxisNamesFromStorage(effectiveAxisNameId);
  }, [effectiveAxisNameId, loadAxisNamesFromStorage]);

  // Nothing here writes minor gridlines from the log scale (EVA-84). A log axis
  // switches them on through the calculated default, which reads the rendered axis
  // type; persisting that would save a consequence of the scale as if it were a user
  // preference, and it could then outlive log mode or clobber a real choice.

  // Product/axis change detection — reset gridline and axis name overrides so defaults re-apply.
  // Tracks X and Y sigs separately so axis name resets are scoped to the axis type that changed:
  // toggling "Merge Same Unit Axes" only changes the X layout, so Y axis name overrides survive.
  // Uses refs to skip the very first load (avoiding a reset on initial mount).
  //
  // MPC-only in practice: the sigs come from layout.xAxis2/yAxis2, which the chart service
  // populates only when OtherProducts is non-empty, so on a single-product chart both are
  // always [] and the guard below returns every time. Single-product charts therefore never
  // reset gridline or axis-name overrides on a field/product switch — do not read this as a
  // self-healing path for stale overrides there (EVA-84).
  const prevXSigRef = useRef<string | null>(null);
  const prevYSigRef = useRef<string | null>(null);
  useEffect(() => {
    // Track the first swatch too: toggling a product can move it without changing the title.
    const xSig = JSON.stringify(
      (apiResponse?.layout?.xAxis2 ?? []).map(
        (ax: { title?: string; productSegments?: AxisProductSegment[] }) => [
          ax.title ?? "",
          firstSwatchedSegmentEnd(ax.title ?? "", ax.productSegments, multiProduct)
        ]
      )
    );
    const ySig = JSON.stringify(
      (apiResponse?.layout?.yAxis2 ?? []).map((ax: { title?: string }) => ax.title ?? "")
    );
    // Layout not loaded yet — or a single-product chart, which never populates these.
    if (xSig === "[]" && ySig === "[]") return;
    if (prevXSigRef.current === null) {
      prevXSigRef.current = xSig;
      prevYSigRef.current = ySig;
      return;
    }
    const xChanged = xSig !== prevXSigRef.current;
    const yChanged = ySig !== prevYSigRef.current;
    if (!xChanged && !yChanged) return;
    prevXSigRef.current = xSig;
    prevYSigRef.current = ySig;
    resetToNull(id); // gridlines always reset on any axis layout change
    if (xChanged && yChanged) {
      axisNameResetToNull(id);
    } else if (xChanged) {
      axisNameResetKeys(id, MPC_X_AXIS_TITLE_KEYS);
    } else {
      axisNameResetKeys(id, MPC_Y_AXIS_TITLE_KEYS);
    }
  }, [
    apiResponse?.layout?.xAxis2,
    apiResponse?.layout?.yAxis2,
    multiProduct,
    id,
    resetToNull,
    axisNameResetToNull,
    axisNameResetKeys
  ]);

  // ref — callback ref fires after mount, avoiding a dispatch-during-render warning
  const overlayRef = useRef<HTMLDivElement>(null);
  const handleOverlayRef = useCallback(
    (node: HTMLDivElement | null) => {
      overlayRef.current = node;
      screenshotDispatch({ payload: { screenshotOverlay: node } });
    },
    [screenshotDispatch]
  );
  // Guard against the render loop: getMergedSettings() always returns a new
  // object reference even when values are identical, which causes this effect
  // to re-fire → Redux dispatch → ChartWrapper re-render → Screenshot
  // re-render → new settings ref → effect fires again (80+ renders).
  // Track the last dispatched key so we skip redundant dispatches.
  const lastDispatchKeyRef = useRef<string>("");

  // derived state
  const screenshotContainer = document.getElementById(containerId);
  const isIndividualScreenshot = !!id && widget?.widgetId === id;
  const isDashboardMode = useIsDashboardScreenshotMode(id);
  const screenshotActive = isIndividualScreenshot || isDashboardMode;

  useEffect(() => {
    // Build a stable key from the values that actually matter.  Skip the
    // dispatch when nothing changed, breaking the reference-equality loop.
    const dispatchKey = `${screenshotActive}:${JSON.stringify(settings)}`;
    if (dispatchKey === lastDispatchKeyRef.current) return;
    lastDispatchKeyRef.current = dispatchKey;

    const next = {
      ...screenshot,
      visible: screenshotActive,
      active: screenshotActive,
      properties: { preset: settings }
    };
    dispatch(
      updateChartByPath({
        id,
        path: `features.screenshot`,
        value: next
      })
    );
    // Only go fullscreen for individual screenshot, not dashboard
    if (!isDashboardMode) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.fullScreen.active`,
          value: isIndividualScreenshot
        })
      );
    }
  }, [dispatch, id, settings, widget, isDashboardMode]);

  // Update the widgets state (only for individual screenshot).
  useEffect(() => {
    if (isIndividualScreenshot) {
      // Copy the options to prevent mutation.
      const optionsCopy = JSON.parse(options);
      screenshotDispatch({
        payload: {
          widgetState: {
            request,
            response: apiResponse,
            options: optionsCopy,
            multiProductActive: multiProduct?.active,
            multiProduct,
            dataRange
          }
        }
      });
    }
  }, [
    screenshotDispatch,
    id,
    widget,
    apiResponse,
    options,
    request,
    multiProduct,
    dataRange
  ]);

  // Dismiss toasts only when first entering screenshot mode, not on every data change.
  useEffect(() => {
    if (isIndividualScreenshot) {
      dismissAllChartToasts(id);
    }
  }, [id, isIndividualScreenshot]);

  // Don't render overlay in dashboard mode
  if (!screenshot.active || !screenshotContainer || isDashboardMode) return null;

  const screenshotBounds = {
    width: settings?.width || 1152,
    height: settings?.height || 681
  };

  return (
    <Portal container={screenshotContainer}>
      <Overlay
        className="screenshot-overlay"
        ref={handleOverlayRef}
        width={screenshotBounds.width}
        height={screenshotBounds.height}>
        {settings?.legendVisible && (
          <ChartLegend
            parentDimensions={screenshotBounds}
            id={containerId + "chart-legend"}
            disableDragging={false}
            showLegendBorder={!!settings?.legendBorderVisible}
            width={250}
            screenshot={screenshot}
            bounds={bounds}
            legend={chartLegend}
          />
        )}
      </Overlay>
    </Portal>
  );
};

export default Screenshot;

const Overlay = styled.div`
  width: calc(${(p) => p.width}px - 2px);
  height: calc(${(p) => p.height}px - 2px);
  outline: 2px solid rgb(255, 208, 0);
  margin: 1px;
  z-index: 1;
  pointer-events: none;
`;
